import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { ArrowRightIcon } from "../icons"

const buttonVariants = cva(
  "group cursor-pointer inline-flex shrink-0 items-center justify-center gap-4 whitespace-nowrap rounded-lg text-lg leading-6 font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-[#383d48]",
        secondary: "bg-secondary text-foreground hover:bg-white",
        ghost: "hover:bg-muted",
        link: "text-brand underline-offset-4 hover:underline",
      },
      size: {
        default: "h-auto w-full px-5 py-[18px]",
        sm: "h-11 px-4 py-2 text-base",
        lg: "h-14 px-5 py-[18px]",
        icon: "size-12",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

export type UIButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    showArrow?: boolean
    endIcon?: React.ReactNode
    startIcon?: React.ReactNode
  }

function Button({
  className,
  variant,
  size,
  asChild = false,
  showArrow,
  endIcon,
  startIcon,
  children,
  ...props
}: UIButtonProps) {
  const Comp = asChild ? Slot : "button"
  const showArrowResolved = showArrow ?? (variant === "primary" && !endIcon)

  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), endIcon && "justify-between", className)}
      {...props}
    >
      {startIcon ? (
        <span className="contents [&>svg]:size-6">{startIcon}</span>
      ) : null}
      <span>{children}</span>
      {endIcon ? (
        <span className="contents [&>svg]:size-6">{endIcon}</span>
      ) : showArrowResolved ? (
        <ArrowRightIcon data-icon="inline-end" size={24} />
      ) : null}
    </Comp>
  )
}
export { Button, buttonVariants }

